export const money = value => new Intl.NumberFormat('mr-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 2,
}).format(Number(value || 0));

export const shortDate = value => value
  ? new Intl.DateTimeFormat('mr-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value))
  : '—';

export const apiErrorMessage = error => {
  const body = error?.response?.data;
  return body?.error || body?.message || error?.message || 'विनंती पूर्ण करता आली नाही.';
};

export const demandBalance = demand => Math.max(
  0,
  Math.round((Number(demand?.total_amount || 0) - Number(demand?.paid_amount || 0)) * 100) / 100
);

export const demandAmountBreakdown = demand => {
  const total = Math.max(0, Number(demand?.total_amount || 0));
  const paid = Math.max(0, Math.min(total, Number(demand?.paid_amount || 0)));
  const penalty = Math.max(0, Math.min(total, Number(demand?.penalty_amount || 0)));
  const principal = Math.max(0, Math.round((total - penalty) * 100) / 100);
  const balance = demandBalance(demand);
  // Statutory policy v1 allocates payments to principal first and never
  // calculates a new penalty on an existing penalty amount.
  const outstandingPrincipal = Math.max(0, Math.round((principal - Math.min(principal, paid)) * 100) / 100);
  const outstandingPenalty = Math.max(0, Math.round((balance - outstandingPrincipal) * 100) / 100);
  return { total, paid, principal, penalty, balance, outstandingPrincipal, outstandingPenalty };
};

export const isOutstandingDemand = demand => (
  ['issued', 'partially_paid', 'overdue', 'under_appeal'].includes(demand?.status) && demandBalance(demand) > 0
);

export const summarizeDemands = demands => (demands || []).reduce((summary, demand) => {
  const amounts = demandAmountBreakdown(demand);
  summary.demand_count += 1;
  summary.total_billed += amounts.total;
  summary.total_paid += amounts.paid;
  summary.total_penalty_assessed += amounts.penalty;
  if (isOutstandingDemand(demand)) {
    summary.open_demand_count += 1;
    summary.outstanding_balance += amounts.balance;
    summary.outstanding_principal += amounts.outstandingPrincipal;
    summary.outstanding_penalty += amounts.outstandingPenalty;
    if (isPayableDemand(demand)) {
      summary.payable_demand_count += 1;
      summary.payable_balance += amounts.balance;
    }
    if (demand.status === 'under_appeal') summary.appeal_hold_balance += amounts.balance;
    if (demand.status === 'overdue') summary.overdue_balance += amounts.balance;
  }
  return summary;
}, {
  demand_count: 0, open_demand_count: 0, payable_demand_count: 0,
  total_billed: 0, total_paid: 0, total_penalty_assessed: 0,
  outstanding_balance: 0, outstanding_principal: 0, outstanding_penalty: 0,
  payable_balance: 0, appeal_hold_balance: 0, overdue_balance: 0,
});

export const isPayableDemand = demand => (
  ['issued', 'partially_paid', 'overdue'].includes(demand?.status) && demandBalance(demand) > 0
);

export const selectedPayableDemands = (demands, selectedUids) => {
  const selected = new Set(selectedUids || []);
  return (demands || []).filter(demand => selected.has(demand.demand_uid) && isPayableDemand(demand));
};

export const paymentAllocations = demands => (demands || []).map(demand => ({
  demand_uid: demand.demand_uid,
  amount: demandBalance(demand),
}));

export const makeIdempotencyKey = prefix => {
  const id = typeof window !== 'undefined' && window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${id}`.slice(0, 100);
};

export const subjectLabel = account => account?.subject?.name
  || account?.account_number
  || account?.account_uid
  || 'कर खाते';

export const statusLabel = status => ({
  draft: 'मसुदा', active: 'सक्रिय', suspended: 'स्थगित',
  closed: 'बंद', archived: 'संग्रहित', issued: 'जारी',
  partially_paid: 'अंशतः भरले', paid: 'पूर्ण भरले', overdue: 'थकीत',
  under_appeal: 'अपील प्रलंबित', cancelled: 'रद्द', written_off: 'निर्लेखित',
  migration_hold: 'स्थलांतर तपासणी', pending: 'प्रलंबित', approved: 'मंजूर',
  rejected: 'नाकारले', revoked: 'मागे घेतले', submitted: 'सादर',
  under_review: 'तपासणी सुरू', hearing: 'सुनावणी',
  allowed: 'मान्य', partially_allowed: 'अंशतः मान्य', withdrawn: 'मागे घेतले',
  completed: 'पूर्ण', preview_ready: 'Preview तयार', requires_reconciliation: 'ताळमेळ आवश्यक',
  resolved: 'निराकरण झाले', failed: 'अयशस्वी',
}[status] || 'स्थिती उपलब्ध नाही');

export const subjectTypeLabel = value => ({
  property: 'मालमत्ता', business: 'व्यवसाय', service_connection: 'सेवा जोडणी',
}[value] || 'करपात्र नोंद');

export const accountClassLabel = value => ({
  property_tax: 'मालमत्ता कर खाते', business_tax: 'व्यवसाय कर खाते', service_charge: 'सेवा शुल्क खाते',
}[value] || 'कर खाते');

export const paymentModeLabel = value => ({
  cash: 'रोख', upi: 'यूपीआय', cheque: 'धनादेश', dd: 'डिमांड ड्राफ्ट',
  bank_transfer: 'बँक हस्तांतरण', razorpay: 'ऑनलाइन भरणा',
}[value] || 'भरणा पद्धत');

export const receiptAllocationSummary = receipt => {
  const allocations = receipt?.allocations || [];
  const taxNames = [...new Set(allocations.map(item => item.tax_name).filter(Boolean))];
  const years = [...new Set(allocations.map(item => item.financial_year).filter(Boolean))]
    .sort((left, right) => String(right).localeCompare(String(left)));
  const taxText = taxNames.join(', ') || 'कर भरणा';
  return years.length ? `${taxText} · आर्थिक वर्ष ${years.join(', ')}` : taxText;
};

export const demandTaxName = demand => demand?.display?.tax_name || accountClassLabel(demand?.account?.account_class);
export const demandSubjectName = demand => demand?.display?.subject_name || 'करपात्र नोंद';
export const demandSubjectReference = demand => demand?.display?.subject_reference || null;

export const groupDemandsByFinancialYear = demands => [...(demands || []).reduce((groups, demand) => {
  const financialYear = demand.financial_year || 'वर्ष उपलब्ध नाही';
  if (!groups.has(financialYear)) groups.set(financialYear, []);
  groups.get(financialYear).push(demand);
  return groups;
}, new Map()).entries()].sort(([left], [right]) => String(right).localeCompare(String(left)));

export const groupDemandsByAccount = demands => [...(demands || []).reduce((groups, demand) => {
  const subjectKey = `${demand.account?.subject_type || 'unknown'}:${demand.account?.subject_id || demand.account?.account_uid || ''}`;
  if (!demand.account?.account_uid) return groups;
  if (!groups.has(subjectKey)) groups.set(subjectKey, {
    subject_key: subjectKey,
    account_uid: demand.account.account_uid,
    account_number: demand.account?.account_number,
    account_uids: [],
    subject_name: demandSubjectName(demand),
    subject_reference: demandSubjectReference(demand),
    demands: [],
  });
  const group = groups.get(subjectKey);
  if (!group.account_uids.includes(demand.account.account_uid)) group.account_uids.push(demand.account.account_uid);
  if (demand.account?.account_class === 'property_tax') {
    group.account_uid = demand.account.account_uid;
    group.account_number = demand.account.account_number;
  }
  group.demands.push(demand);
  return groups;
}, new Map()).values()].map(group => ({
  ...group,
  financial_years: groupDemandsByFinancialYear(group.demands).map(([year]) => year),
  total_amount: group.demands.reduce((total, demand) => total + Number(demand.total_amount || 0), 0),
  principal_amount: group.demands.reduce((total, demand) => total + demandAmountBreakdown(demand).principal, 0),
  penalty_amount: group.demands.reduce((total, demand) => total + Number(demand.penalty_amount || 0), 0),
  paid_amount: group.demands.reduce((total, demand) => total + Number(demand.paid_amount || 0), 0),
  balance_amount: group.demands.reduce((total, demand) => total + demandBalance(demand), 0),
  outstanding_penalty: group.demands.reduce((total, demand) => total + (isOutstandingDemand(demand) ? demandAmountBreakdown(demand).outstandingPenalty : 0), 0),
}));

export const groupTaxAccountsBySubject = (accounts, demands = []) => [...(accounts || []).reduce((groups, account) => {
  const subjectKey = `${account.subject_type || 'unknown'}:${account.subject_id || account.subject?.uid || account.account_uid}`;
  if (!groups.has(subjectKey)) groups.set(subjectKey, []);
  groups.get(subjectKey).push(account);
  return groups;
}, new Map()).entries()].map(([subjectKey, ledgerAccounts]) => {
  const primary = ledgerAccounts.find(account => account.account_class === 'property_tax') || ledgerAccounts[0];
  const scopes = [...new Set(ledgerAccounts.flatMap(account => account.granted_scopes || []))];
  const ledgerUids = new Set(ledgerAccounts.map(account => account.account_uid));
  const subjectDemands = (demands || []).filter(demand => ledgerUids.has(demand.account?.account_uid));
  const apiSummary = {
    open_demand_count: ledgerAccounts.reduce((total, account) => total + Number(account.demand_summary?.open_demand_count || 0), 0),
    payable_demand_count: ledgerAccounts.reduce((total, account) => total + Number(account.demand_summary?.payable_demand_count || 0), 0),
    outstanding_balance: ledgerAccounts.reduce((total, account) => total + Number(account.demand_summary?.outstanding_balance || 0), 0),
    outstanding_principal: ledgerAccounts.reduce((total, account) => total + Number(account.demand_summary?.outstanding_principal || 0), 0),
    outstanding_penalty: ledgerAccounts.reduce((total, account) => total + Number(account.demand_summary?.outstanding_penalty || 0), 0),
    payable_balance: ledgerAccounts.reduce((total, account) => total + Number(account.demand_summary?.payable_balance || 0), 0),
    appeal_hold_balance: ledgerAccounts.reduce((total, account) => total + Number(account.demand_summary?.appeal_hold_balance || 0), 0),
    overdue_balance: ledgerAccounts.reduce((total, account) => total + Number(account.demand_summary?.overdue_balance || 0), 0),
  };
  return {
    ...primary,
    subject_key: subjectKey,
    ledger_accounts: ledgerAccounts,
    granted_scopes: scopes,
    demand_summary: subjectDemands.length ? summarizeDemands(subjectDemands) : apiSummary,
  };
});

export const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const defaultFinancialYear = (date = new Date()) => {
  const year = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return `${year}-${String(year + 1).slice(-2)}`;
};
